import { useEffect, useRef, useState, type ReactNode } from "react";

import type { StreamStatus } from "../api/stream";
import type { RealtimeDiagnosis, SourceHealth, SourceKind } from "../contract/types";
import { useAppGate } from "../auth/AppGate";
import { useConfig } from "../config/ConfigProvider";
import { usePost } from "../posts/PostProvider";
import { formatIsoTime, formatIsoTimeSeconds } from "./format";
import { ThemeToggle } from "./ThemeToggle";
import { useIsMobile } from "./useIsMobile";
import { useLiveSources } from "./useLiveSources";
import { routes, type Screen } from "./useHashRoute";
import brandLockup from "../assets/brand/sentinelle-lockup-white.svg";

const TABS: { screen: Screen; href: string; label: string }[] = [
  { screen: "map", href: routes.map, label: "Carte pilote" },
  { screen: "cockpit", href: routes.cockpit, label: "Régulation" },
  { screen: "network", href: routes.network, label: "Réseau" },
  { screen: "vehicles", href: routes.vehicles, label: "Véhicules" },
  { screen: "services", href: routes.services, label: "Services" },
  { screen: "replay", href: routes.replay, label: "Rejeu" },
  { screen: "stats", href: routes.stats, label: "Statistiques" },
  { screen: "settings", href: routes.settings, label: "Paramètres" },
];

/**
 * Bandeau partagé par les deux écrans : marque (pilotée serveur), onglets-liens
 * et un emplacement à droite propre à chaque écran. La « Carte pilote » est un
 * lien, pas un onglet imbriqué — on peut l'ouvrir sur un second moniteur.
 *
 * **Mobile** (demande utilisateur 2026-09-11, < 769px) : les 8 onglets ne
 * tiennent pas sur une largeur de téléphone — repliés derrière un bouton-icône
 * *menu*, ouvrant une liste verticale (Admin/Déconnexion inclus). Le contenu
 * `right` (propre à chaque écran) passe sur sa propre ligne, pleine largeur,
 * pour ne jamais se faire couper.
 */
export function Topbar({ current, right }: { current: Screen; right: ReactNode }) {
  const config = useConfig();
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = useState(false);
  // Un changement d'écran (clic sur un lien) referme le menu mobile — sinon il
  // resterait ouvert par-dessus le nouvel écran.
  useEffect(() => {
    setMenuOpen(false);
  }, [current]);

  const brand = (
    <div className="navbar-brand" style={{ alignItems: "center", flex: "none", minWidth: 0 }}>
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: 9,
          padding: "0 16px",
          height: 48,
          minWidth: 0,
          borderRight: isMobile ? undefined : "1px solid hsla(0,0%,100%,.14)",
        }}
      >
        {/* « Sentinelle » est le nom du logiciel, constant sur tout déploiement — ce
            n'est pas une donnée de réseau, il n'a pas sa place dans `config`.
            Marque (P2-40) : lockup officiel complet, repli plein texte si le SVG ne charge pas. */}
        <Brand />
        {config.operatingPeriods.today ? (
          <span
            className="ovl"
            title="Régime d'exploitation du jour (CDC §12.3)"
            style={{
              color: "#fff",
              // Fond = couleur du régime quand son nom EST une couleur
              // (ROUGE / VERTE / MAUVE… pour Astuce) ; sinon neutre.
              background: periodColor(config.operatingPeriods.today) ?? "hsla(0,0%,100%,.12)",
              borderRadius: 2,
              padding: "1px 6px",
              letterSpacing: "0.1em",
              flex: "none",
            }}
          >
            {config.operatingPeriods.today}
          </span>
        ) : null}
      </span>
    </div>
  );

  if (isMobile) {
    return (
      <nav
        className="navbar"
        role="navigation"
        aria-label="Bandeau principal"
        style={{ minHeight: 0, background: "var(--sentinelle-ink)", display: "flex", flexDirection: "column" }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          {brand}
          <button
            type="button"
            aria-label={menuOpen ? "Fermer le menu" : "Ouvrir le menu"}
            aria-expanded={menuOpen}
            aria-haspopup="menu"
            onClick={() => setMenuOpen((v) => !v)}
            style={{
              marginInlineStart: "auto",
              marginInlineEnd: 8,
              flex: "none",
              height: 40,
              width: 40,
              padding: 0,
              background: "transparent",
              border: "none",
              color: "#fff",
              cursor: "pointer",
            }}
          >
            <BurgerIcon open={menuOpen} />
          </button>
        </div>

        {right ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 10,
              padding: "6px 12px",
              borderTop: "1px solid hsla(0,0%,100%,.1)",
            }}
          >
            {right}
          </div>
        ) : null}

        {menuOpen ? (
          <div
            role="menu"
            aria-label="Navigation"
            style={{
              borderTop: "1px solid hsla(0,0%,100%,.14)",
              // Trouvé par l'utilisateur (2026-09-11) : sur un écran bas (téléphone
              // en paysage), les 8 liens + Administration/thème/déconnexion peuvent
              // dépasser la hauteur visible — `.mae-app` coupe tout ce qui dépasse
              // (`overflow: hidden`) sans qu'on puisse faire défiler. Le menu déroulant
              // défile désormais lui-même, indépendamment du reste de la page.
              maxHeight: "calc(100vh - 88px)",
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {TABS.map((t) => (
              <TabLink key={t.screen} href={t.href} active={current === t.screen} mobile>
                {t.label}
              </TabLink>
            ))}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 16px",
                borderTop: "1px solid hsla(0,0%,100%,.1)",
              }}
            >
              <a className="ovl" href={routes.admin} style={{ color: "hsla(0,0%,100%,.6)" }}>
                Administration
              </a>
              <ThemeToggle />
            </div>
            <div style={{ padding: "4px 16px 10px" }}>
              <LogoutButton />
            </div>
          </div>
        ) : null}
      </nav>
    );
  }

  return (
    <nav
      className="navbar"
      role="navigation"
      aria-label="Bandeau principal"
      style={{ minHeight: 0, background: "var(--sentinelle-ink)" }}
    >
      {brand}

      <div
        className="navbar-menu is-active"
        style={{ background: "transparent", boxShadow: "none", padding: 0, display: "flex", flex: 1 }}
      >
        <div
          className="navbar-start"
          style={{
            display: "flex",
            flexWrap: "nowrap",
            // Repli trouvé par l'utilisateur (2026-09-11) : entre 769px (seuil
            // mobile) et une largeur d'écran suffisante, les 8 onglets + le
            // contenu `right` ne tiennent pas et étaient tout bonnement coupés,
            // sans aucun moyen de les atteindre. Défilement horizontal tactile
            // en repli, plutôt qu'un clip silencieux.
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            minWidth: 0,
          }}
        >
          {TABS.map((t) => (
            <TabLink key={t.screen} href={t.href} active={current === t.screen}>
              {t.label}
            </TabLink>
          ))}
        </div>
        <div
          className="navbar-end"
          style={{ display: "flex", alignItems: "center", gap: 18, paddingRight: 16, marginInlineStart: "auto" }}
        >
          {right}
          <ThemeToggle />
          <a
            className="ovl"
            href={routes.admin}
            title="Administration de plateforme (compte dédié)"
            style={{ color: "hsla(0,0%,100%,.4)" }}
          >
            Admin
          </a>
          <LogoutButton />
        </div>
      </div>
    </nav>
  );
}

/** Icône *hamburger* ⇄ *croix* (bouton menu mobile), pas de dépendance externe. */
function BurgerIcon({ open }: { open: boolean }) {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true" style={{ display: "block", margin: "auto" }}>
      {open ? (
        <path d="M4 4 L16 16 M16 4 L4 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      ) : (
        <>
          <line x1="3" y1="5" x2="17" y2="5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <line x1="3" y1="10" x2="17" y2="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <line x1="3" y1="15" x2="17" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </>
      )}
    </svg>
  );
}

/**
 * « Se déconnecter » de la porte d'accès applicative (contrat v1.38 §8bis,
 * brief §6). Masqué tant qu'aucune session n'est établie (porte dormante :
 * `username` nul) — rien à déconnecter dans ce cas.
 */
function LogoutButton() {
  const { username, logout } = useAppGate();
  const [busy, setBusy] = useState(false);
  if (!username) return null;
  return (
    <button
      type="button"
      className="ovl"
      onClick={() => {
        if (busy) return;
        setBusy(true);
        void logout().finally(() => setBusy(false));
      }}
      disabled={busy}
      title={`Connecté : ${username} — se déconnecter`}
      style={{
        background: "transparent",
        border: "none",
        color: "hsla(0,0%,100%,.4)",
        cursor: "pointer",
        padding: "2px 4px",
      }}
    >
      Se déconnecter
    </button>
  );
}

/**
 * Couleur d'un régime d'exploitation quand son NOM est une couleur (Astuce :
 * ROUGE / VERTE / MAUVE…). Réseau-agnostique : on ne mappe que des mots de
 * couleur français, `null` sinon (badge neutre). Nuances assombries pour rester
 * lisibles en blanc.
 */
const PERIOD_COLORS: [RegExp, string][] = [
  [/rouge/i, "#c0392b"],
  [/verte?/i, "#1e824c"],
  [/bleue?/i, "#2563eb"],
  [/mauve|violet/i, "#8e44ad"],
  [/jaune|dor[ée]/i, "#b7950b"],
  [/orange/i, "#ca6510"],
  [/gris(e)?/i, "#5d6d7e"],
  [/noire?/i, "#1c1c1c"],
  [/marron|brun/i, "#6e4b3a"],
  [/rose/i, "#c2185b"],
];
function periodColor(label: string): string | null {
  return PERIOD_COLORS.find(([re]) => re.test(label))?.[1] ?? null;
}

/**
 * Marque Sentinelle (P2-40) : le lockup officiel `sentinelle-lockup-white.svg`
 * (écusson + mot-symbole blanc « SENTINELLE », fourni par l'utilisateur
 * 2026-09-10) — choix utilisateur pour le bandeau. Si le SVG échoue au
 * chargement, repli plein texte (accent `--sentinelle-rail` + « SENTINELLE »),
 * pour ne jamais laisser le bandeau sans identité.
 *
 * Renommage produit 2026-09-10 : « Maestro » → « Sentinelle » (court) /
 * « Sentinelle AAD » (Anticipateur et Aide à la Décision) — puis 2026-09-12 :
 * « Sentinelle DSS » (Decision Support System, forme longue retenue —
 * catégorie de système déjà établie, cf.
 * https://en.wikipedia.org/wiki/Decision_support_system).
 *
 * **2026-09-12 (soir)** : la baseline (portait jusque-là la forme longue en
 * toutes lettres sous le mot-symbole) est retirée du lockup — le bandeau ne
 * montre plus que « SENTINELLE ». Fichier remplacé par l'officiel fourni par
 * l'utilisateur (`sentinelle_logo_white_no_text.svg`), où le mot-symbole est
 * un **tracé vectorisé** plutôt que du texte `<font-family>` — rendu robuste,
 * indépendant des polices installées côté client. Repli plein texte inchangé
 * (jamais eu de baseline).
 */
function Brand() {
  const [broken, setBroken] = useState(false);

  if (broken) {
    return (
      <span style={{ display: "flex", alignItems: "center", gap: 9, height: 30 }}>
        <span style={{ width: 9, height: 22, background: "var(--sentinelle-rail)", flex: "none" }} aria-hidden />
        <span style={{ font: "700 15px/1 var(--bulma-family-secondary)", letterSpacing: "0.16em", color: "#fff" }}>
          SENTINELLE
        </span>
      </span>
    );
  }

  return (
    <img
      src={brandLockup}
      alt="Sentinelle DSS"
      onError={() => setBroken(true)}
      style={{ display: "block", height: 30, width: "auto", flex: "none" }}
    />
  );
}

/**
 * `mobile` : rendu du menu déplié (pleine largeur, empilé, cible tactile
 * généreuse — 48px de haut est déjà confortable, on garde la même valeur).
 * L'indicateur d'onglet actif passe du soulignement (desktop, sous un rang de
 * texte) au liseré latéral gauche (plus visible en liste verticale).
 *
 * Exporté (2026-09-12) : réutilisé tel quel par `admin/AdminConsole.tsx` pour
 * sa propre sous-navigation (Bascules/Sources/…) — l'onglet du bandeau
 * principal ET celui du panneau admin doivent avoir le même habillage, signalé
 * par l'utilisateur comme une vraie différence de style entre les deux.
 */
export function TabLink({
  href,
  active,
  mobile,
  children,
}: {
  href: string;
  active: boolean;
  mobile?: boolean;
  children: string;
}) {
  return (
    <a
      className="navbar-item mono"
      href={href}
      aria-current={active ? "page" : undefined}
      style={{
        height: 48,
        display: "flex",
        alignItems: "center",
        padding: mobile ? "0 16px" : "0 15px",
        fontSize: mobile ? "0.78rem" : "0.68rem",
        fontWeight: 600,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: active ? "#fff" : "hsla(0,0%,100%,.6)",
        background: active ? "hsla(0,0%,100%,.07)" : "transparent",
        borderBottom: mobile ? "1px solid hsla(0,0%,100%,.08)" : `2px solid ${active ? "var(--sentinelle-rail)" : "transparent"}`,
        borderLeft: mobile ? `3px solid ${active ? "var(--sentinelle-rail)" : "transparent"}` : undefined,
      }}
    >
      {children}
    </a>
  );
}

/**
 * Indicateur « Temps réel » (P2-30, D-020). Couleur / texte pilotés par
 * `realtime.verdict` + `realtime.headline` du backend (REST, rafraîchi ~45 s).
 * Repli sur l'état du canal SSE tant que le 1er diagnostic n'est pas arrivé.
 * **Cliquable** : ouvre la fenêtre « d'où vient la panne » — tout le texte de
 * diagnostic vient du serveur, le frontend n'en compose aucun.
 *
 * **Dynamique selon le poste actif** (signalement utilisateur 2026-09-19) :
 * `realtime` est un verdict **réseau entier**, sans notion de poste — une
 * panne de source propre à UN exploitant (ex. Cityway qui ne publie plus TNI)
 * affichait « Temps réel dégradé » même pour un poste qui ne régule que
 * Transdev Rouen. `sources[]`, lui, porte `operatorId`/`affectedLineIds` par
 * source : on ne retient que les sources dont une ligne touchée croise
 * `activeLineIds` (l'isolement du poste, `PostProvider`) pour décider la
 * pastille. Exception : une panne **systémique** (notre propre ingestion/
 * moteur à l'arrêt, ou `culprit: "sentinelle"`) reste affichée à tout le
 * monde — ce n'est pas une panne de source par opérateur, donc pas scopable.
 */
export function StreamBadge({ status }: { status: StreamStatus }) {
  const { sources, realtime } = useLiveSources();
  const { activeLineIds } = usePost();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Poste sans isolement (aucun poste, ou poste couvrant tout le réseau) :
  // toutes les sources sont pertinentes, comportement inchangé.
  const relevantSources = activeLineIds
    ? sources.filter((s) => s.affectedLineIds.some((id) => activeLineIds.includes(id)))
    : sources;
  const systemic = realtime ? realtime.ingest.stalled || realtime.engine.stalled || realtime.culprit === "sentinelle" : false;
  const scopedVerdict: RealtimeDiagnosis["verdict"] | null =
    !realtime || systemic || !activeLineIds
      ? (realtime?.verdict ?? null)
      : relevantSources.some((s) => s.status === "down")
        ? "down"
        : relevantSources.some((s) => s.fault != null || s.fallback === "scheduled")
          ? "degraded"
          : "nominal";

  const streamLost = status === "closed" || status === "reconnecting";
  const base = describeStreamStatus(status);

  let color = base.color;
  let label = base.label;
  let blink = false;
  if (realtime) {
    if (scopedVerdict === "down") {
      color = "#ef4444";
      blink = true;
      label = "Temps réel indisponible";
    } else if (scopedVerdict === "degraded") {
      color = "#f59e0b";
      blink = true;
      label = "Temps réel dégradé";
    } else {
      color = "#22c55e";
      label = "Temps réel";
    }
    // Le détail (`headline`, causes, battements de cœur) est dans la fenêtre au
    // clic — la pastille reste courte pour ne pas déborder de la barre.
  } else if (streamLost || relevantSources.some((s) => s.fallback === "scheduled")) {
    color = "#ef4444";
    label = streamLost ? base.label : "Temps réel dégradé";
    blink = true;
  }

  return (
    <div ref={box} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={realtime ? realtime.headline : `Flux temps réel : ${base.label}`}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 7,
          background: "transparent",
          border: "none",
          padding: "2px 4px",
          cursor: "pointer",
          maxWidth: 260,
        }}
      >
        <span
          aria-hidden
          style={{
            flex: "none",
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: color,
            ...(blink ? { animation: "mae-blink 1s steps(1) infinite" } : {}),
          }}
        />
        <span
          className="ovl"
          style={{
            color: blink ? "#fca5a5" : "hsla(0,0%,100%,.6)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {label}
        </span>
      </button>

      {open ? (
        <RealtimeDiagnosisPanel realtime={realtime} sources={sources} status={status} onClose={() => setOpen(false)} />
      ) : null}
    </div>
  );
}

const SOURCE_KIND_LABEL: Record<SourceKind, string> = {
  "vehicle-positions": "positions véhicules",
  "trip-updates": "horaires temps réel",
  alerts: "alertes",
  "gtfs-static": "GTFS statique",
};

/**
 * Fenêtre « d'où vient la panne » (D-020). Aucune phrase de diagnostic n'est
 * composée ici : `headline`, `detail`, `evidence` viennent du backend. Le
 * littéral `realtime.culprit` (`"sentinelle"` / `"cityway"`) n'est **jamais**
 * affiché (renommage produit 2026-09-10) — l'origine est déjà portée par
 * `headline`.
 */
function RealtimeDiagnosisPanel({
  realtime,
  sources,
  status,
  onClose,
}: {
  realtime: RealtimeDiagnosis | null;
  sources: SourceHealth[];
  status: StreamStatus;
  onClose: () => void;
}) {
  const faulty = sources.filter((s) => s.fault != null);

  return (
    <div
      role="dialog"
      aria-label="État du temps réel"
      style={{
        position: "absolute",
        right: 0,
        top: "calc(100% + 6px)",
        width: 340,
        maxWidth: "90vw",
        background: "var(--sentinelle-surface)",
        color: "var(--sentinelle-ink)",
        border: "1px solid var(--sentinelle-rule)",
        borderRadius: 6,
        boxShadow: "0 8px 26px rgba(0,0,0,.28)",
        padding: "12px 14px",
        zIndex: 20,
        fontSize: "0.78rem",
        lineHeight: 1.45,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
        <strong style={{ flex: 1, fontSize: "0.82rem" }}>
          {realtime ? realtime.headline : "Diagnostic temps réel indisponible"}
        </strong>
        <button
          type="button"
          className="button is-small is-white"
          aria-label="Fermer"
          onClick={onClose}
          style={{ height: 30, width: 30, padding: 0, flex: "none", lineHeight: 1 }}
        >
          ✕
        </button>
      </div>

      {realtime ? (
        <div style={{ marginTop: 8, display: "grid", gap: 3 }}>
          <Heartbeat label="Ingestion GTFS-RT" hb={realtime.ingest} />
          <Heartbeat label="Moteur de courses" hb={realtime.engine} />
          <div className="has-text-grey" style={{ fontSize: "0.72rem" }}>
            Flux navigateur : {describeStreamStatus(status).label.toLowerCase()}
          </div>
        </div>
      ) : null}

      {faulty.length > 0 ? (
        <div style={{ marginTop: 10, borderTop: "1px solid var(--sentinelle-rule)", paddingTop: 8, display: "grid", gap: 8 }}>
          {faulty.map((s) => (
            <div key={s.sourceId}>
              <div style={{ fontWeight: 700 }}>
                {s.operatorId} · {SOURCE_KIND_LABEL[s.kind]}
              </div>
              <div>{s.fault!.detail}</div>
              {s.fault!.since ? (
                <div className="has-text-grey" style={{ fontSize: "0.72rem" }}>
                  depuis {formatIsoTime(s.fault!.since)}
                </div>
              ) : null}
              <div className="has-text-grey" style={{ fontSize: "0.72rem", fontStyle: "italic" }}>
                {s.fault!.evidence}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {realtime ? (
        <div className="has-text-grey" style={{ marginTop: 10, fontSize: "0.7rem" }}>
          Diagnostic établi à {formatIsoTimeSeconds(realtime.observedAt)}.
        </div>
      ) : null}
    </div>
  );
}

function Heartbeat({ label, hb }: { label: string; hb: RealtimeDiagnosis["ingest"] }) {
  const txt = hb.stalled
    ? "à l'arrêt"
    : hb.ageSeconds != null
      ? `OK, il y a ${hb.ageSeconds} s`
      : "état inconnu";
  return (
    <div style={{ display: "flex", gap: 6 }}>
      <span style={{ minWidth: 128 }}>{label} :</span>
      <span style={{ fontWeight: 600, color: hb.stalled ? "var(--sentinelle-signal)" : "var(--sentinelle-ok)" }}>{txt}</span>
    </div>
  );
}

function describeStreamStatus(status: StreamStatus): { label: string; color: string } {
  switch (status) {
    case "open":
      return { label: "Temps réel", color: "#22c55e" };
    case "connecting":
      return { label: "Connexion…", color: "#f59e0b" };
    case "reconnecting":
      return { label: "Reconnexion…", color: "#f97316" };
    case "closed":
      return { label: "Flux fermé", color: "#ef4444" };
    default:
      return { label: "En attente", color: "#9aa7b4" };
  }
}

export function TopbarClock() {
  const now = useNow();
  return (
    <span className="mono" style={{ fontSize: "1.05rem", fontWeight: 700, color: "#fff" }} aria-label="Horloge du poste">
      {new Intl.DateTimeFormat("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }).format(now)}
    </span>
  );
}

function useNow(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}
